import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Users,
  Mail,
  Phone,
  ArrowLeft,
  MessageCircle,
  XCircle,
  AlertTriangle,
  Info,
} from "lucide-react";
import * as api from "../services/api";
import { subscribeToPushNotifications } from "../utils/pushNotifications";
import { useBranding } from "../contexts/BrandingContext";
import { useTranslation } from "react-i18next";
import { useBusinessLabels } from "../hooks/useBusinessLabels";
import type { BusinessType } from "../types";
import LoadingScreen from "../components/ui/LoadingScreen";
import IosInstallBanner from "../components/ui/IosInstallBanner";
import PoweredBy from "../components/ui/PoweredBy";
import YulqoMark from "../components/ui/YulqoMark";
import { getEventJoinBlock } from "../utils/eventJoin";
import { mapEventError } from "../utils/mapEventError";
import styles from "./JoinQueuePage.module.css";

const JoinQueuePage: React.FC = () => {
  const { isLoading: brandingLoading, applyOrganizationTheme } = useBranding();
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const eventId = searchParams.get("eventId");

  const [event, setEvent] = useState<Awaited<ReturnType<typeof api.getEvent>> | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [joinBlock, setJoinBlock] = useState<ReturnType<typeof getEventJoinBlock>>(null);
  const [showPoweredBy, setShowPoweredBy] = useState(true); // default true = safe
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [partySize, setPartySize] = useState(1);
  const [iosBanner, setIosBanner] = useState<null | "notSafari" | "safari">(null);

  const businessType: BusinessType =
    (event?.organization?.businessType as BusinessType) ||
    (eventId
      ? (sessionStorage.getItem(`businessType_${eventId}`) as BusinessType)
      : undefined) ||
    "OTHER";
  const labels = useBusinessLabels(businessType);

  useEffect(() => {
    const ua = navigator.userAgent;
    const isIOS = /iphone|ipad|ipod/i.test(ua);
    if (!isIOS) return;

    const isStandalone = window.matchMedia("(display-mode: standalone)").matches;
    if (isStandalone) return;

    const isSafari =
      /safari/i.test(ua) && !/crios|fxios|opios|mercury|chrome/i.test(ua);

    setIosBanner(isSafari ? "safari" : "notSafari");
  }, []);

  useEffect(() => {
    if (!eventId) {
      navigate("/", { replace: true });
      return;
    }

    const existingClientId = localStorage.getItem(`clientId_${eventId}`);
    if (existingClientId) {
      navigate(`/status?clientId=${existingClientId}`, { replace: true });
      return;
    }

    const fetchEvent = async () => {
      try {
        const eventData = await api.getEvent(eventId);
        if (!eventData) throw new Error(t("common.error"));
        const block = getEventJoinBlock(eventData);
        if (block) {
          setJoinBlock(block);
        }
        setEvent(eventData);
        const bt = eventData.organization?.businessType;
        if (bt && eventId) {
          sessionStorage.setItem(`businessType_${eventId}`, bt);
        }

        const orgId =
          eventData.organizationId ||
          eventData.artistId ||
          (eventId ? sessionStorage.getItem(`queueOrgId_${eventId}`) : null);
        if (orgId) {
          try {
            const orgBranding = await api.getBranding(orgId);
            if (orgBranding) {
              applyOrganizationTheme({
                primaryColor: orgBranding.primaryColor,
                secondaryColor: orgBranding.secondaryColor,
              });
              setShowPoweredBy(orgBranding.poweredByVisible !== false);
            }
          } catch {
            /* keep current theme */
          }
        }
      } catch (err: unknown) {
        const axiosErr = err as { response?: { data?: { error?: string } }; message?: string };
        setError(
          axiosErr.response?.data?.error ||
            axiosErr.message ||
            "Événement introuvable",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [eventId, navigate, t, applyOrganizationTheme]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!event) return;
    setSubmitting(true);
    setError("");

    try {
      const client = await api.joinQueue({
        name,
        email,
        organizationId: event.organizationId || event.artistId,
        eventId: event.id,
        phone: phone || undefined,
        whatsappEnabled: !!phone && event.whatsappNotifications,
        emailEnabled: event.emailNotifications,
        pushEnabled: event.pushNotifications,
        language: i18n.language || "en",
        ...(businessType === "RESTAURANT" && { partySize }),
      });

      try {
        await subscribeToPushNotifications(client.id);
      } catch {
        /* push optional */
      }

      localStorage.setItem(`clientId_${event.id}`, client.id);
      navigate(`/status?clientId=${client.id}`, { replace: true });
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string; code?: string; details?: { eventName?: string; startDate?: string } } } };
      const apiError = axiosErr.response?.data;
      if (apiError?.code) {
        const mapped = mapEventError(apiError, t, i18n.language);
        setError(mapped.message);
      } else {
        setError(apiError?.error || "Erreur lors de l'inscription");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || brandingLoading) {
    return <LoadingScreen message={t("common.loading")} />;
  }

  if (!event && !loading) {
    return (
      <div className={styles.page}>
        <div className={styles.wrap}>
          <div className={styles.errorCard}>
            <AlertTriangle className={styles.errorCardIcon} aria-hidden="true" />
            <h2 className={styles.errorCardTitle}>{t("common.error")}</h2>
            <p className={styles.errorCardText}>{t("join.alreadyJoined")}</p>
            <button type="button" onClick={() => navigate("/")} className={styles.submitBtn}>
              {t("common.back")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!event) return null;

  if (joinBlock) {
    const blocked = mapEventError(
      { code: joinBlock.code, details: { eventName: joinBlock.eventName, startDate: joinBlock.startDate } },
      t,
      i18n.language,
    );

    return (
      <div className={styles.page}>
        <div className={styles.wrap}>
          <div className={styles.card}>
            <div className={styles.alertError}>
              <Info className={styles.errorIcon} aria-hidden="true" />
              <span>{blocked.message}</span>
            </div>
            <button type="button" onClick={() => navigate("/")} className={styles.backBtn}>
              <ArrowLeft className="w-3.5 h-3.5" aria-hidden="true" />
              <span>{t("common.back")}</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {iosBanner && (
        <IosInstallBanner variant={iosBanner} onDismiss={() => setIosBanner(null)} />
      )}

      <div className={styles.wrap}>
        <div className={styles.card}>
          <span className={styles.step}>{t("look.step2")}</span>
          <div className={styles.place}>
            <strong>{event.name}</strong>
            {event.description ? <small>{event.description}</small> : null}
          </div>
          <form onSubmit={handleSubmit} className={styles.form}>
            <div>
              <label htmlFor="name" className={styles.label}>
                <Users className={styles.labelIcon} aria-hidden="true" />
                {t("join.name")} <span className={styles.required}>*</span>
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("join.namePlaceholder")}
                className={styles.input}
                required
              />
            </div>

            <div>
              <label htmlFor="email" className={styles.label}>
                <Mail className={styles.labelIcon} aria-hidden="true" />
                {t("join.email")} <span className={styles.required}>*</span>
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("join.emailPlaceholder") || "your@email.com"}
                className={styles.input}
                required
              />
            </div>

            <div>
              <label htmlFor="phone" className={styles.label}>
                <Phone className={styles.labelIcon} aria-hidden="true" />
                {t("join.phone")}
                {!event.phoneRequired && (
                  <span className={styles.optional}>
                    ({t("common.optional") || "optional"})
                  </span>
                )}
                {event.phoneRequired && (
                  <span className={styles.required}>*</span>
                )}
              </label>
              <input
                id="phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder={t("join.phonePlaceholder") || "+33 6 12 34 56 78"}
                className={styles.input}
                required={event.phoneRequired}
              />
              <p className={styles.phoneHint}>
                <MessageCircle className="w-3.5 h-3.5" aria-hidden="true" />
                {phone ? t("join.whatsappInfo") : t("join.phoneHint")}
              </p>
            </div>

            {/* Nombre de personnes — uniquement pour les restaurants */}
            {businessType === "RESTAURANT" && (
              <div>
                <label htmlFor="partySize" className={styles.label}>
                  👥 {t("join.partySize", "Pour combien de personnes ?")}
                  <span className={styles.required}>*</span>
                </label>
                <select
                  id="partySize"
                  value={partySize}
                  onChange={(e) => setPartySize(parseInt(e.target.value))}
                  className={styles.input}
                  required
                >
                  {Array.from(
                    { length: event.maxPartySize ?? 8 },
                    (_, i) => i + 1,
                  ).map((n) => (
                    <option key={n} value={n}>
                      {n} {n === 1 ? t("join.person", "personne") : t("join.persons", "personnes")}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {error && (
              <div className={styles.alertError}>
                <XCircle className={styles.errorIcon} aria-hidden="true" />
                <span>{error}</span>
              </div>
            )}

            <button type="submit" disabled={submitting} className={styles.submitBtn}>
              {submitting ? (
                <>
                  <YulqoMark size={18} spinning />
                  <span>{t("join.submitting")}</span>
                </>
              ) : (
                <span>{labels.joinTitle}</span>
              )}
            </button>
          </form>

          <button type="button" onClick={() => navigate("/")} className={styles.backBtn}>
            <ArrowLeft className="w-3.5 h-3.5" aria-hidden="true" />
            <span>{t("common.back")}</span>
          </button>
        </div>
        <div className={styles.footer}>
          {showPoweredBy && <PoweredBy />}
        </div>
      </div>
    </div>
  );
};

export default JoinQueuePage;
